// Web search front-end. Drives two surfaces from one compiled bundle:
//   • the dedicated /search page          (<body data-search="page">)
//   • the header search box on doc views   (<body> contains #doc-search)
//
// Both do the same thing: debounced search-as-you-type against the session-authed
// JSON endpoint (GET /search.json?q=…), client-rendered results, and ↑/↓/Enter
// keyboard selection. ⌘K / Ctrl-K focuses the box on either surface.
//
// Result snippets arrive as server-sanitized HTML (ts_headline highlight), so
// they're inserted as trusted markup; every other field is escaped here.

type Result = {
  organization: string;
  username: string;
  slug: string;
  url: string;
  title: string;
  format: "markdown" | "html";
  snippet: string; // trusted HTML (escaped + <mark> by the server)
  semantic: boolean;
};

type SearchResponse = {
  query: string;
  degraded: boolean;
  results: Result[];
};

const DEBOUNCE_MS = 200;

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

function ownerName(username: string): string {
  return username
    .split("-")
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");
}

// Color-coded file-type label. Markdown → sky, HTML → amber (Catalyst Badge
// color tokens, light + dark). `shrink-0` so it survives the title's truncate.
function formatBadge(format: Result["format"]): string {
  const isHtml = format === "html";
  const label = isHtml ? "HTML" : "MD";
  const color = isHtml
    ? "bg-amber-400/20 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400"
    : "bg-sky-500/15 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300";
  return `<span class="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${color}">${label}</span>`;
}

// Full result card for the dedicated page. No relevance score — order is the
// only ranking signal we expose.
function cardHTML(r: Result, index: number): string {
  return `<a href="${escape(r.url)}" target="_blank" rel="noopener" role="option" data-result data-index="${index}"
      class="group block rounded-xl border border-transparent px-4 py-3.5 -mx-4 transition-colors hover:border-zinc-950/10 hover:bg-zinc-50 dark:hover:border-white/10 dark:hover:bg-white/5">
      <div class="flex items-center gap-x-2 min-w-0">
        <span class="min-w-0 font-semibold text-zinc-950 truncate group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">${escape(r.title)}</span>
        ${formatBadge(r.format)}
      </div>
      <div class="mt-0.5 flex items-center gap-x-1.5 text-xs text-zinc-400 dark:text-zinc-500">
        <span>${escape(ownerName(r.username))}</span><span>·</span><span class="font-mono">${escape(r.username)}/${escape(r.slug)}</span>
      </div>
      <p class="mt-1.5 text-sm text-zinc-500 line-clamp-2 dark:text-zinc-400">${r.snippet}</p>
    </a>`;
}

// Compact row for the header dropdown.
function rowHTML(r: Result, index: number): string {
  return `<a href="${escape(r.url)}" target="_blank" rel="noopener" role="option" data-result data-index="${index}"
      class="block px-4 py-2.5 transition-colors hover:bg-zinc-50 dark:hover:bg-white/5">
      <div class="flex items-center justify-between gap-x-2">
        <div class="flex min-w-0 items-center gap-x-1.5">
          <span class="min-w-0 text-sm font-medium text-zinc-900 truncate dark:text-zinc-100">${escape(r.title)}</span>
          ${formatBadge(r.format)}
        </div>
        <span class="shrink-0 text-xs text-zinc-300 dark:text-zinc-600 font-mono">${escape(r.username)}</span>
      </div>
      <p class="mt-0.5 text-xs text-zinc-400 truncate dark:text-zinc-500">${r.snippet}</p>
    </a>`;
}

const ACTIVE_CLASS = ["bg-zinc-100", "dark:bg-white/10", "border-zinc-950/10", "dark:border-white/10"];

// Roving ↑/↓/Enter selection over the rendered results, shared by both surfaces.
class KeyboardNav {
  private index = -1;

  constructor(
    input: HTMLInputElement,
    private container: HTMLElement,
    private onEnterEmpty: () => void,
  ) {
    input.addEventListener("keydown", (e) => this.onKey(e));
  }

  private items(): HTMLElement[] {
    return Array.from(this.container.querySelectorAll<HTMLElement>("[data-result]"));
  }

  reset(): void {
    this.index = -1;
    this.paint();
  }

  private paint(): void {
    this.items().forEach((el, i) => {
      const active = i === this.index;
      el.classList.toggle("border", active);
      for (const c of ACTIVE_CLASS) el.classList.toggle(c, active);
      el.setAttribute("aria-selected", String(active));
      if (active) el.scrollIntoView({ block: "nearest" });
    });
  }

  private onKey(e: KeyboardEvent): void {
    const items = this.items();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.index = Math.min(this.index + 1, items.length - 1);
      this.paint();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.index = Math.max(this.index - 1, -1);
      this.paint();
    } else if (e.key === "Enter") {
      const active = items[this.index];
      if (active) {
        e.preventDefault();
        window.open((active as HTMLAnchorElement).href, "_blank", "noopener"); // results open in a new tab
      } else {
        this.onEnterEmpty();
      }
    }
  }
}

// Debounced, cancellable fetch of the JSON endpoint.
function makeSearcher() {
  let timer: number | undefined;
  let controller: AbortController | undefined;

  return (query: string, degraded: boolean): Promise<SearchResponse | null> =>
    new Promise((resolve) => {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const params = new URLSearchParams({ q: query });
        if (degraded) params.set("degrade", "1"); // dev-only knob; ignored in prod
        try {
          const res = await fetch(`/search.json?${params}`, {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          });
          resolve(res.ok ? ((await res.json()) as SearchResponse) : null);
        } catch {
          resolve(null); // aborted or network error — caller keeps prior UI
        }
      }, DEBOUNCE_MS);
    });
}

function searchPath(query: string): string {
  return `/search?q=${encodeURIComponent(query)}`;
}

function degradedFlag(): boolean {
  // Optional dev affordance: a checkbox#degrade simulates the embedder being down.
  return (document.querySelector<HTMLInputElement>("#degrade")?.checked) ?? false;
}

// ── Dedicated /search page ──────────────────────────────────────────────────
function initPage(): void {
  const input = document.querySelector<HTMLInputElement>("#q");
  const results = document.querySelector<HTMLElement>("#results");
  const suggestions = document.querySelector<HTMLElement>("#suggestions");
  const note = document.querySelector<HTMLElement>("#degraded-note");
  if (!input || !results) return;

  const searcher = makeSearcher();
  const nav = new KeyboardNav(input, results, () => {
    const first = results.querySelector<HTMLAnchorElement>("[data-result]");
    if (first) window.open(first.href, "_blank", "noopener");
  });

  const render = async () => {
    const query = input.value.trim();
    // Keep the URL shareable/bookmarkable without reloading.
    const url = query ? searchPath(query) : "/search";
    history.replaceState(null, "", url);

    if (!query) {
      results.innerHTML = "";
      suggestions?.classList.remove("hidden");
      note?.classList.add("hidden");
      nav.reset();
      return;
    }
    suggestions?.classList.add("hidden");

    const data = await searcher(query, degradedFlag());
    if (!data || data.query !== query) return; // stale / failed response
    note?.classList.toggle("hidden", !data.degraded);

    if (data.results.length === 0) {
      results.innerHTML = `<div class="py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">No documents match <span class="font-medium text-zinc-700 dark:text-zinc-300">&ldquo;${escape(query)}&rdquo;</span>.<br><span class="text-zinc-400 dark:text-zinc-500">Try fewer or more general words.</span></div>`;
    } else {
      results.innerHTML = data.results.map(cardHTML).join("");
    }
    nav.reset();
  };

  input.addEventListener("input", render);
  document.querySelector("#degrade")?.addEventListener("change", render);
  for (const chip of document.querySelectorAll<HTMLButtonElement>("[data-chip]")) {
    chip.addEventListener("click", () => {
      input.value = chip.textContent ?? "";
      input.focus();
      render();
    });
  }
  bindFocusHotkey(input);

  // Hydrate from ?q= (arrived via the header box's "see all", a shared link, etc.)
  const q0 = new URLSearchParams(location.search).get("q");
  if (q0) {
    input.value = q0;
    render();
  }
}

// ── Header search box on document views ─────────────────────────────────────
function initHeader(wrap: HTMLElement): void {
  const input = wrap.querySelector<HTMLInputElement>("#q");
  const dropdown = wrap.querySelector<HTMLElement>("#dropdown");
  const note = document.querySelector<HTMLElement>("#degraded-note");
  if (!input || !dropdown) return;

  const searcher = makeSearcher();
  const nav = new KeyboardNav(input, dropdown, () => {
    if (input.value.trim()) window.location.href = searchPath(input.value.trim());
  });

  const render = async () => {
    const query = input.value.trim();
    if (!query) {
      dropdown.classList.add("hidden");
      note?.classList.add("hidden");
      nav.reset();
      return;
    }
    const data = await searcher(query, degradedFlag());
    if (!data || data.query !== query) return;
    note?.classList.toggle("hidden", !data.degraded);

    if (data.results.length === 0) {
      dropdown.innerHTML = `<div class="px-4 py-6 text-center text-sm text-zinc-400 dark:text-zinc-500">No matches for &ldquo;${escape(query)}&rdquo;</div>`;
    } else {
      dropdown.innerHTML =
        data.results.map(rowHTML).join("") +
        `<a href="${searchPath(query)}" class="block border-t border-zinc-950/5 px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:bg-white/5">See all results for &ldquo;${escape(query)}&rdquo; <span class="text-zinc-400 dark:text-zinc-500">— press Enter</span> &rarr;</a>`;
    }
    dropdown.classList.remove("hidden");
    nav.reset();
  };

  input.addEventListener("input", render);
  input.addEventListener("focus", () => { if (input.value.trim()) render(); });
  document.querySelector("#degrade")?.addEventListener("change", render);
  document.addEventListener("click", (e) => {
    if (!wrap.contains(e.target as Node)) dropdown.classList.add("hidden");
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      dropdown.classList.add("hidden");
      input.blur();
    }
  });
  bindFocusHotkey(input);
}

// ⌘K / Ctrl-K focuses (and selects) the search box on either surface.
function bindFocusHotkey(input: HTMLInputElement): void {
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });
}

function boot(): void {
  if (document.body.dataset.search === "page") {
    initPage();
    return;
  }
  const header = document.querySelector<HTMLElement>("#doc-search");
  if (header) initHeader(header);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
